import { ColDef, GridApi, GridReadyEvent, IDatasource, IGetRowsParams } from 'ag-grid-community';
import 'ag-grid-community/dist/styles/ag-grid.css';
import { StyledAgGridReact, TftfSelect } from 'assets/theme/StyledComponents';
import { FC, useEffect, useState } from 'react';
import Select, { ValueType } from 'react-select';
import { subscribeListener, unsubscribeListener } from 'redux/ably/sagas';
import { getChartData } from 'redux/chart/api';
import { StyledGridWrapper } from '../../assets/theme/StyledGridWrapper';
import Calendar from './Calendar';
import OrderSideCellRenderer from './OrderSideCellRenderer';

interface IESAStMomoInstruments { label: string, value: string}
const items: IESAStMomoInstruments[] = [
  { label: 'ES - 1Min', value: 'VPMES1' },
  { label: 'ES - 5Min', value: 'VPMES5' },
  { label: 'ES - 60Min', value: 'VPMES60' },
  { label: 'NQ - 1Min', value: 'VPMNQ1' },
  { label: 'NQ - 5Min', value: 'VPMNQ5' },
  { label: 'NQ - 60Min', value: 'VPMNQ60' },
];


const columnDef: ColDef[] = [
  {
    cellRenderer: "loadingRenderer",
    field: 'time',
    headerName: 'Time',
    sort: 'desc',
  },
  {
    field: 'close',
    headerName: 'Close',
  },
  {
    field: 'hrv',
    headerName: 'HRV',
  },
  {
    field: 'avg_hrv',
    headerName: 'Avg HRV',
  },
  {
    field: 'percentile95',
    headerName: 'Percentile 95',
  },
  {
    field: 'percentile90',
    headerName: 'Percentile 90',
  },
  {
    field: 'percentile10',
    headerName: 'Percentile 10',
  },
  {
    field: 'trade_signal',
    headerName: 'Trade Signal',
  },
  {
    field: 'trend_count',
    headerName: 'Trend Count',
  },
  {
    field: 'stop_out',
    headerName: 'Stop Out',
  },
  {
    field: 'stop_out_trend_count',
    headerName: 'S/O Trend Count',
  },
  {
    field: 'trade_signal_final',
    headerName: 'Signal Final',
  },
  {
    cellRendererFramework: OrderSideCellRenderer,
    field: 'stmomo',
    headerName: 'Trend',
  },
  {
    field: 'initial_entry_point',
    headerName: 'Initial Entry',
  },
  {
    field: 'second_entry_point',
    headerName: 'Second Entry',
  },
  {
    field: 'third_entry_point',
    headerName: 'Third Entry',
  },
  {
    field: 'projected_stop_out',
    headerName: 'Projected Stop Out',
  },
  {
    field: 'tftf90',
    headerName: 'TFTF 90',
  },
  {
    field: 'tftf10',
    headerName: 'TFTF 10',
  },
  // {
  //   cellRendererFramework: OrderSideCellRenderer,
  //   field: 'watch',
  //   headerName: 'Watch',
  // },
  // {
  //   field: 'tftfIndicator',
  //   headerName: 'TFTF Indicator',
  // },
  // {
  //   cellRendererFramework: OrderSideCellRenderer,
  //   field: 'tftfSignal',
  //   headerName: 'TFTF Signal',
  // },
  // {
  //   field: 'IVOLDailyRange',
  //   headerName: 'IVOL Daily Range',
  // },
  // {
  //   field: 'breadth',
  //   headerName: 'Breadth',
  // },
  // {
  //   field: 'date',
  //   filter: 'agDatzeColumnFilter',
  //   filterParams: {
  //     comparator: (filterLocalDateAtMidnight: Date, cellValue: string) => {
  //       const dateAsString = cellValue;
  //       const dateParts = dateAsString.split('/');
  //       const cellDate = new Date(Number(dateParts[2]), Number(dateParts[1]) - 1, Number(dateParts[0]));
  //       if (filterLocalDateAtMidnight.getTime() === cellDate.getTime()) {
  //         return 0;
  //       }
  //       if (cellDate < filterLocalDateAtMidnight) {
  //         return -1;
  //       }
  //       if (cellDate > filterLocalDateAtMidnight) {
  //         return 1;
  //       }
  //       return;
  //     }
  //   },
  //   headerName: 'Date',
  //   width: 190,
  // }
];

interface IESASTMomoConfig {
  model: string,
  symbol: string
}

export const ESASTMomo: FC<{id: number}> = ({ id }) => {
  const [esastmomoConfig, setEsastmomoConfig] = useState<IESASTMomoConfig>({ symbol: 'ES', model: 'VPMES1' });
  const [gridApi, setGridApi] = useState<GridApi>()

  useEffect(() => {
    const config = localStorage.getItem(`esaitem-${id}`)
    if (config) {
      setEsastmomoConfig(JSON.parse(config));
    }
  }, [id])

  useEffect(() => {
    subscribeListener('store:connected', id, ({ data }: any) => {
      if (gridApi && data === WebSocket.OPEN) {
        gridApi.refreshInfiniteCache()
      }
    });

    return () => unsubscribeListener('store:connected', id);
  }, [gridApi, id]);

  useEffect(() => {
    subscribeListener(`stmomo:${esastmomoConfig.model}`, id, () => {
      if (gridApi) {
        gridApi.refreshInfiniteCache()
      }
    });
    return () => unsubscribeListener(`stmomo:${esastmomoConfig.model}`, id);
  }, [esastmomoConfig, gridApi, id]);

  useEffect(() => {
    if (gridApi) {
    const dataSource: IDatasource = {
      getRows: async (params: IGetRowsParams) => {
        let query=`stmomo?model=${esastmomoConfig.model}&startRow=${params.startRow}&endRow=${params.endRow}`
        if (params.sortModel && params.sortModel.length > 0) {
          query+=`&sort_col=${params.sortModel[0].colId}&dir=${params.sortModel[0].sort}`
        }
        try {
          const results = await getChartData(query)
          params.successCallback(results.data)
        } catch (e) {
          params.failCallback()
        }
      },
      rowCount: undefined,
    }
    gridApi.setDatasource(dataSource)
    }
  }, [gridApi, esastmomoConfig])

	const onSelectChanged = (item: ValueType<IESAStMomoInstruments, false>) => {
    if (!item) { return; }
    if (gridApi) {
      gridApi.purgeInfiniteCache()
    }
    if (Array.isArray(item)) { return; }
    const selectedItem = item as IESAStMomoInstruments
    const selectedSymbol = selectedItem.label.split(' ')[0];
    const newConfig = {
      model: selectedItem.value,
      symbol: selectedSymbol,
    }
    localStorage.setItem(`esaitem-${id}`, JSON.stringify(newConfig))
    setEsastmomoConfig(newConfig)
  };

  const onGridReady = (event: GridReadyEvent) => {
    const restoredState = localStorage.getItem(`esaconfig-${id}`)
    if (restoredState) {
      try {
        const parsedState: any = JSON.parse(restoredState)
        event.columnApi.setColumnState(parsedState);
      } catch (e) {
        // TODO
      }
    } else {
      event.api.sizeColumnsToFit()
    }
    setGridApi(event.api)
    event.api.addGlobalListener(() => {
      const savedState = event.columnApi.getColumnState();
      localStorage.setItem(`esaconfig-${id}`, JSON.stringify(savedState))
    })
  }

  return (
    <StyledGridWrapper fixedRowHeight={true}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
        <TftfSelect>
          <Select<{ label: string, value: string}>
              placeholder="Choose a symbol"
              className="react-select"
              classNamePrefix="react-select"
              value={items.find(item => item.value === esastmomoConfig.model)}
              options={items}
              isClearable={false}
              suppressDragLeaveHidesColumns={true}
              onChange={onSelectChanged} />
        </TftfSelect>
      {/* <Calendar /> */}
      <div />
      </div>
      <div style={{ height: 'calc(100% - 40px)'}}>
      <StyledAgGridReact
        frameworkComponents={{ agDateInput: Calendar }}
        components={{
          loadingRenderer: (params) => {
            if (params.value !== undefined) {
              return new Date(params.value * 1000).toLocaleString()
            } else {
              return `<div style={{ position: 'absolute', display: 'block', height: '20px', width: '20px', left: '50%', top: '50%' }}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="20" height="20" fill="white">
                  <path opacity=".25" d="M16 0 A16 16 0 0 0 16 32 A16 16 0 0 0 16 0 M16 4 A12 12 0 0 1 16 28 A12 12 0 0 1 16 4"/>
                  <path d="M16 0 A16 16 0 0 1 32 16 L28 16 A12 12 0 0 0 16 4z">
                    <animateTransform attributeName="transform" type="rotate" from="0 16 16" to="360 16 16" dur="0.8s" repeatCount="indefinite" />
                  </path>
                </svg>
              </div>`
            }
          }
        }}
        // reactNext={true}
        columnDefs={columnDef}
        onGridReady={onGridReady}
        animateRows={true}
        defaultColDef={{
          resizable: true,
          sortable: true,
        }}
        rowModelType='infinite'
        paginationPageSize={25}
        infiniteInitialRowCount={25}
        maxBlocksInCache={10}
        icons={{
          sortAscending: '<i class="fa fa-sort-up"/>',
          sortDescending: '<i class="fa fa-sort-down"/>'
        }}
      />
      </div>
    </StyledGridWrapper>
  );
};
